import { queryOptions, useQuery } from '@tanstack/react-query';
import { ActivityService, MaterialClassificationDTO } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { STALE_TIME_LIVE_VIEW } from '../../queryClient';
import { MaterialClassification, transformMaterialClassification } from '../materialClassification';

export const activityMaterialClassificationKeys = {
  base: () => ['activityMaterialClassification'] as const,
  one: ({ jobsiteId, captureStartAt }: ActivityMaterialClassificationOptionProps) =>
    [...activityMaterialClassificationKeys.base(), jobsiteId, captureStartAt] as const,
};

export interface ActivityMaterialClassificationOptionProps {
  jobsiteId: string;
  captureStartAt: string;
}

export function activityMaterialClassificationOptions({
  jobsiteId,
  captureStartAt,
  enabled,
}: ActivityMaterialClassificationOptionProps & { enabled: boolean }) {
  const transformFn = (resp: MaterialClassificationDTO) => transformMaterialClassification(resp?.output || []);
  return queryOptions<MaterialClassification, ApiError>({
    queryKey: activityMaterialClassificationKeys.one({ jobsiteId, captureStartAt }),
    queryFn: async () => {
      if (!jobsiteId || !captureStartAt) {
        throw new Error('jobsiteId and captureStartAt are required');
      }

      const response = await ActivityService.activityControllerGetMaterialClassification({
        path: { jobsiteId, capture_start_at: captureStartAt },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!captureStartAt && enabled,
    staleTime: STALE_TIME_LIVE_VIEW,
  });
}

export function useActivityMaterialClassification(
  props: ActivityMaterialClassificationOptionProps & { enabled: boolean }
) {
  return useQuery(activityMaterialClassificationOptions(props));
}
